import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import axios from 'axios';

import { decodeJwtPayload } from '../../Auth/session';
import { useEntitlements, subscribeTier, cancelSubscription } from '../../services/entitlementsService';
import { merchantChatEnabled, merchantChatStart, merchantChatMessage, merchantChatPoll } from '../../urls/api-shipments/crm';

const ACCENT = '#F15A29';
const POLL_INTERVAL_MS = 5000;
const BOT_NAME = 'Bill';

const authHeaders = () => ({
  Authorization: 'Bearer ' + (localStorage.getItem('access_token') || ''),
  'content-type': 'application/json',
});

// Pull a friendly name/email from the signed-in id_token so the CRM inbox shows
// who is chatting when a conversation escalates to a human.
const currentIdentity = () => {
  const payload = decodeJwtPayload(localStorage.getItem('id_token')) || {};
  const name = payload.name || [payload.given_name, payload.family_name].filter(Boolean).join(' ') || payload.nickname || '';
  return { name: name || null, email: payload.email || null };
};

const BillChat = () => {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [liveAgent, setLiveAgent] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [justUpgraded, setJustUpgraded] = useState(false);
  const [planPanelOpen, setPlanPanelOpen] = useState(false);

  const conversationIdRef = useRef(null);
  const lastTsRef = useRef(null);
  const seenIdsRef = useRef({});
  const bodyRef = useRef(null);
  const nodeRef = useRef(null);

  // Bill is a Pro feature: the bubble shows for everyone, but on the free tier
  // clicking it opens an upgrade prompt instead of the chat. Unknown/loading is
  // treated as unlocked so paid users never see a flash of "locked".
  const entitlements = useEntitlements();
  const billUnlocked = justUpgraded || !(entitlements && entitlements.features && entitlements.features.bill_assistant === false);
  const currentTier = (entitlements && entitlements.tier) || 'free';

  // Only show Bill where the backend says it's enabled (Integrity during rollout).
  useEffect(() => {
    let active = true;
    axios
      .get(merchantChatEnabled, { headers: authHeaders() })
      .then(res => {
        const enabled = res && res.data && res.data.payload ? res.data.payload.enabled : res && res.data ? res.data.enabled : false;
        if (active) setEligible(Boolean(enabled));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const ingest = useCallback(list => {
    if (!Array.isArray(list) || list.length === 0) return;
    setMessages(prev => {
      const next = [...prev];
      list.forEach(m => {
        const id = m && m.crm_message_id;
        if (id) {
          if (seenIdsRef.current[id]) return;
          seenIdsRef.current[id] = 1;
        }
        next.push(m);
        if (m && m.created_at) lastTsRef.current = m.created_at;
      });
      return next;
    });
  }, []);

  const start = useCallback(() => {
    if (started) return;
    setStarted(true);
    const identity = currentIdentity();
    axios
      .post(merchantChatStart, { name: identity.name, email: identity.email, page_url: window.location.href }, { headers: authHeaders() })
      .then(res => {
        const p = (res && res.data && res.data.payload) || (res && res.data) || {};
        conversationIdRef.current = p.conversation_id || null;
        setLiveAgent(Boolean(p.live_agent));
        ingest(p.messages || []);
      })
      .catch(() => {
        ingest([{ crm_message_id: 'err-start', sender: 'bot', body: 'Sorry, I could not start the chat. Please try again.' }]);
      });
  }, [started, ingest]);

  // Poll for bot/agent replies while the panel is open.
  useEffect(() => {
    if (!open || !started) return undefined;
    const timer = setInterval(() => {
      const conversationId = conversationIdRef.current;
      if (!conversationId) return;
      const params = { conversation_id: conversationId };
      if (lastTsRef.current) params.after = lastTsRef.current;
      axios
        .get(merchantChatPoll, { headers: authHeaders(), params })
        .then(res => {
          const p = (res && res.data && res.data.payload) || (res && res.data) || {};
          setLiveAgent(Boolean(p.live_agent));
          ingest(p.messages || []);
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [open, started, ingest]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  const send = () => {
    const text = draft.trim();
    const conversationId = conversationIdRef.current;
    if (!text || !conversationId || sending) return;
    setSending(true);
    ingest([{ crm_message_id: `local-${Date.now()}`, sender: 'visitor', body: text }]);
    setDraft('');
    axios
      .post(merchantChatMessage, { conversation_id: conversationId, text }, { headers: authHeaders() })
      .then(res => {
        const p = (res && res.data && res.data.payload) || (res && res.data) || {};
        ingest(p.messages || []);
      })
      .catch(() => {
        ingest([{ crm_message_id: `err-${Date.now()}`, sender: 'bot', body: 'Something went wrong sending that. Please try again.' }]);
      })
      .finally(() => setSending(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && billUnlocked) start();
  };

  // Self-serve plan change: charge the saved card/ACH for the chosen paid tier.
  // From Free this unlocks Bill in place; a cross-grade (Pro<->Enterprise) reloads
  // so every lock badge reflects the new tier.
  const handleChangePlan = tier => {
    if (upgrading) return;
    const wasLocked = !billUnlocked;
    setUpgrading(true);
    setUpgradeError('');
    subscribeTier(tier)
      .then(result => {
        if (!result.ok) {
          setUpgradeError(result.message);
          return;
        }
        if (wasLocked) {
          setJustUpgraded(true);
          start();
        } else {
          window.location.reload();
        }
      })
      .finally(() => setUpgrading(false));
  };

  // Downgrade to Free: cancels the Stripe subscription, then reloads to re-lock
  // paid features.
  const handleDowngradeFree = () => {
    if (upgrading) return;
    setUpgrading(true);
    setUpgradeError('');
    cancelSubscription()
      .then(result => {
        if (!result.ok) {
          setUpgradeError(result.message);
          return;
        }
        window.location.reload();
      })
      .finally(() => setUpgrading(false));
  };

  if (!eligible) return null;

  return (
    <Draggable nodeRef={nodeRef} handle=".bill-drag-handle" cancel=".bill-no-drag" bounds="body">
      <div
        ref={nodeRef}
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2147483000, fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' }}
      >
        {open && !billUnlocked && (
          <div
            style={{
              width: 320,
              maxWidth: 'calc(100vw - 32px)',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 12px 40px rgba(0,0,0,.28)',
              overflow: 'hidden',
              marginBottom: 14,
            }}
          >
            <div
              className="bill-drag-handle"
              style={{ background: ACCENT, color: '#fff', padding: '12px 16px', fontWeight: 600, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'move' }}
            >
              <span>Meet {BOT_NAME}</span>
              <button
                type="button"
                className="bill-no-drag"
                aria-label="Close"
                onClick={toggleOpen}
                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            <div className="bill-no-drag" style={{ padding: 18, color: '#1a1a1a' }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{BOT_NAME} is a Pro feature</div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: '#444' }}>
                Upgrade to Pro and let {BOT_NAME} track your orders, pull proof of delivery, quote, and book deliveries for you — just by chatting.
              </div>
              <button
                type="button"
                onClick={() => handleChangePlan('pro')}
                disabled={upgrading}
                style={{ marginTop: 14, width: '100%', background: ACCENT, color: '#fff', border: 'none', borderRadius: 20, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: upgrading ? 'default' : 'pointer', opacity: upgrading ? 0.7 : 1 }}
              >
                {upgrading ? 'Working…' : 'Upgrade to Pro — $99/mo'}
              </button>
              <button
                type="button"
                onClick={() => handleChangePlan('enterprise')}
                disabled={upgrading}
                style={{ marginTop: 8, width: '100%', background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 20, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: upgrading ? 'default' : 'pointer', opacity: upgrading ? 0.7 : 1 }}
              >
                Upgrade to Enterprise — $299/mo
              </button>
              <div style={{ marginTop: 8, fontSize: 12, color: '#777' }}>
                Billed monthly to your card or bank on file. Cancel anytime.
              </div>
              {upgradeError ? (
                <div style={{ marginTop: 10, fontSize: 13, color: '#c0392b' }}>
                  {upgradeError}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {open && billUnlocked && (
          <div
            style={{
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              height: 520,
              maxHeight: 'calc(100vh - 140px)',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 12px 40px rgba(0,0,0,.28)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              marginBottom: 14,
            }}
          >
            <div
              className="bill-drag-handle"
              style={{
                background: ACCENT,
                color: '#fff',
                padding: '12px 16px',
                fontWeight: 600,
                fontSize: 15,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'move',
              }}
            >
              <span>{BOT_NAME} · Gophr Assistant</span>
              <button
                type="button"
                className="bill-no-drag"
                aria-label="Close chat"
                onClick={toggleOpen}
                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div className="bill-no-drag" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: '#fff', borderBottom: '1px solid #eef0f2', fontSize: 12, color: '#555' }}>
              <span>Plan: <strong style={{ color: '#1a1a1a' }}>{currentTier === 'enterprise' ? 'Enterprise' : currentTier === 'pro' ? 'Pro' : 'Free'}</strong></span>
              <button type="button" onClick={() => setPlanPanelOpen(v => !v)} style={{ background: 'transparent', border: 'none', color: ACCENT, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {planPanelOpen ? 'Close' : 'Manage plan'}
              </button>
            </div>
            {planPanelOpen && (
              <div className="bill-no-drag" style={{ padding: '10px 12px', background: '#fafbfc', borderBottom: '1px solid #eef0f2' }}>
                {currentTier !== 'enterprise' ? (
                  <button type="button" onClick={() => handleChangePlan('enterprise')} disabled={upgrading} style={{ width: '100%', marginBottom: 8, background: ACCENT, color: '#fff', border: 'none', borderRadius: 18, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: upgrading ? 'default' : 'pointer', opacity: upgrading ? 0.7 : 1 }}>
                    Upgrade to Enterprise — $299/mo
                  </button>
                ) : (
                  <button type="button" onClick={() => handleChangePlan('pro')} disabled={upgrading} style={{ width: '100%', marginBottom: 8, background: ACCENT, color: '#fff', border: 'none', borderRadius: 18, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: upgrading ? 'default' : 'pointer', opacity: upgrading ? 0.7 : 1 }}>
                    Switch to Pro — $99/mo
                  </button>
                )}
                <button type="button" onClick={handleDowngradeFree} disabled={upgrading} style={{ width: '100%', background: 'transparent', color: '#c0392b', border: '1px solid #e2b6b0', borderRadius: 18, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: upgrading ? 'default' : 'pointer', opacity: upgrading ? 0.7 : 1 }}>
                  {upgrading ? 'Working…' : 'Downgrade to Free'}
                </button>
                {upgradeError ? <div style={{ marginTop: 8, fontSize: 12, color: '#c0392b' }}>{upgradeError}</div> : null}
              </div>
            )}

            <div ref={bodyRef} className="bill-no-drag" style={{ flex: 1, overflowY: 'auto', padding: 14, background: '#f7f8fa' }}>              {messages.map(m => {
                const isVisitor = m.sender === 'visitor';
                return (
                  <div key={m.crm_message_id} style={{ marginBottom: 10, display: 'flex', justifyContent: isVisitor ? 'flex-end' : 'flex-start' }}>
                    <div
                      style={{
                        maxWidth: '82%',
                        padding: '9px 12px',
                        borderRadius: 12,
                        fontSize: 14,
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        background: isVisitor ? ACCENT : '#fff',
                        color: isVisitor ? '#fff' : '#1a1a1a',
                        border: isVisitor ? 'none' : '1px solid #e6e8eb',
                        borderBottomRightRadius: isVisitor ? 4 : 12,
                        borderBottomLeftRadius: isVisitor ? 12 : 4,
                      }}
                    >
                      {!isVisitor && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginBottom: 2 }}>
                          {m.sender === 'agent' ? m.agent_name || 'Gophr Team' : BOT_NAME}
                        </div>
                      )}
                      {m.body}
                    </div>
                  </div>
                );
              })}
            </div>

            {liveAgent && (
              <div className="bill-no-drag" style={{ fontSize: 11, color: '#8a94a6', textAlign: 'center', padding: 6 }}>
                Connected with a Gophr team member
              </div>
            )}

            <div className="bill-no-drag" style={{ borderTop: '1px solid #e6e8eb', padding: 10, display: 'flex', gap: 8, background: '#fff' }}>
              <input
                type="text"
                value={draft}
                placeholder={`Message ${BOT_NAME}...`}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') send();
                }}
                style={{ flex: 1, border: '1px solid #d5d8dc', borderRadius: 20, padding: '9px 14px', fontSize: 14, outline: 'none', color: '#1a1a1a' }}
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !draft.trim()}
                style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 20, padding: '0 16px', fontSize: 14, cursor: 'pointer', opacity: sending || !draft.trim() ? 0.6 : 1 }}
              >
                Send
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          className="bill-drag-handle"
          aria-label={open ? 'Close chat' : 'Chat with Bill'}
          onClick={toggleOpen}
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: ACCENT,
            color: '#fff',
            border: 'none',
            boxShadow: '0 6px 20px rgba(0,0,0,.25)',
            cursor: 'pointer',
            fontSize: 26,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
          }}
        >
          {open ? '×' : '💬'}
        </button>
      </div>
    </Draggable>
  );
};

export default BillChat;
